import { useState } from "react";
import { motion } from "framer-motion";
import { Send, MessageCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const WA_LINK = "https://wa.me/919035083242?text=Hi%20TheKhoj!%20I%27d%20like%20to%20know%20more%20about%20the%20Career%20Exploration%20Program.";

const initial = { student_name: "", parent_name: "", relation: "", grade: "", phone: "", email: "", message: "" };

const inputCls = "w-full rounded-xl border border-input bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring transition-shadow";

export default function EnquiryForm() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Thank you! Your enquiry has been received. We will reach out shortly.");
      setForm(initial);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 sm:py-28" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-primary font-semibold mb-4">Begin The Journey</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
              Let's discover what your child <span className="text-accent">truly loves.</span>
            </h2>
            <p className="mt-5 text-muted-foreground text-base sm:text-lg leading-relaxed max-w-md">
              Share a few details and we'll call you back to schedule the free orientation conversation. No pressure, no obligation — just a warm chat about possibilities.
            </p>
            <div className="mt-8 space-y-4">
              <a href={WA_LINK} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link"
                className="flex items-center gap-4 bg-card border border-border rounded-2xl p-5 hover:shadow-lg transition-shadow">
                <span className="w-11 h-11 rounded-full bg-[#25D366]/15 text-[#1da851] flex items-center justify-center">
                  <MessageCircle className="w-5 h-5" />
                </span>
                <span>
                  <span className="block font-semibold text-sm">Ping us on WhatsApp</span>
                  <span className="block text-xs text-muted-foreground">+91 9035083242</span>
                </span>
              </a>
              <div className="flex items-center gap-4 bg-card border border-border rounded-2xl p-5" data-testid="contact-email-card">
                <span className="w-11 h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                  <Send className="w-5 h-5" />
                </span>
                <span>
                  <span className="block font-semibold text-sm">Write to us</span>
                  <span className="block text-xs text-muted-foreground">shreecarpediem@gmail.com</span>
                </span>
              </div>
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="bg-card border border-border rounded-3xl p-7 sm:p-9 shadow-xl shadow-primary/5 space-y-5"
            data-testid="enquiry-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-semibold mb-1.5">Student Name *</label>
                <input required value={form.student_name} onChange={set("student_name")} className={inputCls} placeholder="e.g. Aarav Sharma" data-testid="enquiry-form-student-name" />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1.5">Your Name *</label>
                <input required value={form.parent_name} onChange={set("parent_name")} className={inputCls} placeholder="Parent / Guardian name" data-testid="enquiry-form-parent-name" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-semibold mb-1.5">You are the student's *</label>
                <select required value={form.relation} onChange={set("relation")} className={inputCls} data-testid="enquiry-form-relation-select">
                  <option value="">Select relation</option>
                  <option>Mother</option>
                  <option>Father</option>
                  <option>Guardian</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1.5">Student Grade *</label>
                <select required value={form.grade} onChange={set("grade")} className={inputCls} data-testid="enquiry-form-grade-select">
                  <option value="">Select grade</option>
                  <option>Grade 9</option>
                  <option>Grade 10</option>
                  <option>Grade 11</option>
                  <option>Grade 12</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-semibold mb-1.5">Contact Number *</label>
                <input required type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="WhatsApp enabled number" data-testid="enquiry-form-phone-input" />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1.5">Email ID *</label>
                <input required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" data-testid="enquiry-form-email-input" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1.5">Describe what's on your mind please</label>
              <textarea rows={3} value={form.message} onChange={set("message")} className={inputCls} placeholder="Anything you'd like us to know…" data-testid="enquiry-form-message-input" />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="enquiry-form-submit-button"
              className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-primary text-primary-foreground font-semibold text-sm hover:opacity-90 transition-opacity disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {loading ? "Sending…" : "Submit Enquiry"}
            </button>
            <p className="text-[11px] text-muted-foreground text-center">We respond within 48 hours. Your details stay private.</p>
            <p className="text-[10px] text-muted-foreground/70 leading-relaxed text-center border-t border-border pt-4" data-testid="enquiry-disclaimer">
              The career exploration practitioner needs the contact information you provide to contact you about career
              exploration and related services. You voluntarily provide consent to share your personal information and
              may unsubscribe from our communications and services at any time.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
